import { Checkbox, Container, Grid, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { ConfirmDialog, DataGridStyles, Topbar, Page } from 'components';
import { observer } from 'mobx-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { IInvoicingLicense, Services } from 'types';
import { MainSidebar } from '../views';
import client from '../feathers'
import { DatagridPlaceholder } from 'components/datagridPlaceholder';

export interface IStatisticsProps { }

/**
 * Licenses section for analysts where licenses can be marked as invoiced
 *
 * @type Analyst
 */

export const Clients = observer((props: IStatisticsProps) => {
    const { t } = useTranslation();
    const [licenses,   setLicenses]   = React.useState<IInvoicingLicense[]>([]);
    const [curLicense, setCurLicense] = React.useState<IInvoicingLicense>(null);

    React.useEffect(() => {
      try {
        client.service(Services.License).find().then((l: IInvoicingLicense[]) => {
          setLicenses(l?.map(_l => ({
            ..._l,
            billed_at:     _l.billed_at    ? new Date(_l.billed_at)    : undefined,
            activated_at:  _l.activated_at ? new Date(_l.activated_at) : undefined,
          })) || []);
        });
      } catch(err) {
        console.error("Failed to load licenses", err);
        setLicenses([]);
      }
    }, []);
    
    const columns: GridColDef[] = [
      {
        field:             'company_title',
        headerName:        t('company'),
        sortable:          true,
        flex:              2,
        headerClassName:   'columnHeader',
      },{
        field:             'user_name',
        headerName:        t('user'),
        sortable:          true,
        flex:              1,
        headerClassName:   'columnHeader',
      },{
        field:             'content_type',
        headerName:        t('content_type'),
        sortable:          true,
        flex:              1,
        headerClassName:   'columnHeader',
      },{
        field:             'content_title',
        headerName:        t('content_title'),
        sortable:          true,
        flex:              1,
        headerClassName:   'columnHeader',
      },{
        field:             'activated_at',
        headerName:        t('activation_date'),
        type:              'dateTime',
        flex:              1,
        headerClassName:   'columnHeader',
      },{
        field:             'billed_at',
        headerName:        t('billing_date'),
        type:              'dateTime',
        flex:              1,
        headerClassName:   'columnHeader',
      },{
        field:             'invoiced',
        headerName:        t('invoiced'),
        sortable:          true,
        flex:              1,
        align:             'left',
        maxWidth:          100,
        headerClassName:   'actionHeader',
        renderCell:      (params: GridRenderCellParams<IInvoicingLicense, IInvoicingLicense>) => (
          <Checkbox 
            checked  = {params.row.invoiced}
            disabled = {params.row.invoiced}
            onChange = {() => setCurLicense(params.row)}
          />
        ),
      },
    ];

    return (
      <Page sidebar={<MainSidebar />} topbar={<Topbar />}>
        <Container sx={{padding:{xs: 4}, maxWidth:{lg: 'lg'}}}>
          <Grid
            item
            xs             = {12}
            mb             = {3}
            display        = 'flex'
            justifyContent = 'space-between'
          >
            <Typography variant='h2'>{t('clients')}</Typography>
          </Grid>
          <DataGridStyles>
            <DataGrid
              autoHeight
              disableSelectionOnClick
              disableColumnSelector
              components = {{
                NoRowsOverlay: () =>
                (<DatagridPlaceholder title={t('datagrid_placeholder_analyst_client_list')}/>)
              }}
              rowHeight          = {66}
              density            = 'standard'
              rows               = {licenses || []}
              columns            = {columns}
              pageSize           = {10}
              rowsPerPageOptions = {[10]}
            />
          </DataGridStyles>
      </Container>
      <ConfirmDialog 
        open              = {Boolean(curLicense)}
        onClose           = {() => setCurLicense(null)}
        title             = {t('confirm_invoice_license_modal_title') + '?'}
        content           = {t('confirm_invoice_license_modal_message')}
        confirmButtonText = {t('confirm')}
        cancelButtonText  = {t('cancel')}
        onCancel          = {() => setCurLicense(null)}
        onConfirm         = {async () => {
          try {
            const res =  await client.service(Services.License).patch(curLicense.id, {content_type: curLicense.content_type});
            if(!res) throw new Error("Failed to update license");
            
            curLicense.invoiced  = true;
            curLicense.billed_at = new Date();
          } catch(err) {
            console.error("Failed to mark license as invoiced", err);
          }
          setCurLicense(null);
        }}
      />
    </Page>
  );
});
